import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { Worker } from '../../domain/models/worker.model';
import { Drawer } from '../../domain/models/drawer.model';
import { Location } from '../../domain/models/location.model';
import { SERVICE_STORE } from '../../domain/tokens/service-store.token';
import { takeUntilDestroyed} from '@angular/core/rxjs-interop';
import { ServiceViewModel, WorkerCardConfig } from '../view-model/service.view-model';
import { FeedbackPort } from '@platform/feedback/domain/ports/feedback.port';
import { ServiceMapper } from '../mappers/service.mapper';
import { SelectLocationUseCase } from '../use-cases/select-location.use-case';
import { SelectWorkerUseCase } from '../use-cases/select-worker.use-case';
import { SelectCashDrawerUseCase } from '../use-cases/select-cash-drawer.use-case';
import { FetchServiceUseCase } from '../use-cases/fetch-service.use-case';
import { ChangeDrawerUseCase } from '../use-cases/change-drawer.use-case';
import { RequestWorkerPasswordUseCase } from '../use-cases/request-worker-password.use-case';
import { first } from 'rxjs';
import { FeatureFlagAdapter } from '@shared/infrastructure/adapters/feature-flag.adapter';
import { WORKER_FLAGS } from '../feature-flags/worker-flags.enum';

@Injectable({ providedIn: 'root' })
export class ServiceFacade {
  private readonly destroyRef = inject(DestroyRef);
  private readonly selectLocationUseCase = inject(SelectLocationUseCase);
  private readonly selectCashDrawerUseCase = inject(SelectCashDrawerUseCase);
  private readonly selectWorkerUseCase = inject(SelectWorkerUseCase);
  private readonly fetchServiceUseCase = inject(FetchServiceUseCase);
  private readonly changeDrawerUseCase = inject(ChangeDrawerUseCase);
  private readonly requestWorkerPasswordUseCase = inject(RequestWorkerPasswordUseCase);
  private readonly featureFlagAdapter = inject(FeatureFlagAdapter);
  private readonly store = inject(SERVICE_STORE);
  private readonly mapper = inject(ServiceMapper);

  private readonly feedback = inject(FeedbackPort);

  private readonly workerCardConfig = signal<WorkerCardConfig>({
    useBiggerAvatar: false,
  });

  readonly viewModel = computed<ServiceViewModel>(
    () => this.mapper.toVM(this.store.state(), this.workerCardConfig())
  );

  private readonly autoSelectSingleLocationEffect = effect(
    () => {
      const selection = this.store.state().selection;
      const viewModel = this.viewModel();

      if (selection?.location) {
        return;
      }

      if (viewModel.locations.length === 1) {
        this.selectLocationUseCase.execute(viewModel.locations[0]);
      }
    },
    { allowSignalWrites: true }
  );

  showWorkerViewFirst(newIsWorkersOwnedDevice: boolean): void {
    const {isWorkersOwnedDevice} = this.store.state();

    if (isWorkersOwnedDevice !== newIsWorkersOwnedDevice) {
      this.store.setIsWorkersOwnedDevice(newIsWorkersOwnedDevice);
    }
  }

  clearStore(): void {
    this.store.reset();
  }

  init(): void {
    this.loadWorkerCardFeatureFlags();

    const isInitialState = this.store.checkIfInitialState();

    if (!isInitialState) {
      return;
    }

    this.store.setIsLoading(true);

    this.feedback.showLoading();
    this.fetchServiceUseCase.execute().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      complete: () => {
        this.store.setIsLoading(false);
        this.feedback.hideLoading();
      },
      error: () => {
        this.store.setIsLoading(false);
        this.feedback.hideLoading();
      }
    });
  }

  private loadWorkerCardFeatureFlags(): void {
    this.featureFlagAdapter.onFeatureFlagsLoaded$
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.workerCardConfig.set({
          useBiggerAvatar: this.featureFlagAdapter.isFlagActivated(WORKER_FLAGS.enableBiggerWaiterImage),
        });
      });
  }

  selectLocation(location: Location): void {
    this.selectLocationUseCase.execute(location);
  }

  selectDrawer(drawer: Drawer): void {
    this.selectCashDrawerUseCase.execute(drawer);
  }

  selectWorker(worker: Worker): void {
    this.selectWorkerUseCase.execute(worker);
  }

  requestWorkerPassword(worker: Worker): void {
    this.requestWorkerPasswordUseCase
      .execute(worker)
      .pipe(
        first(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((success: boolean) => {
        if (success) {
          this.selectWorkerUseCase.execute(worker);
        }
      });
  }

  resetSelection(): void {
    this.store.setSelection({
      location: null,
      drawer: null,
      worker: null
    });
  }

  changeDrawer(): void {
    this.changeDrawerUseCase.execute();
  }
}
